<template>
  <transition v-cloak>
    <div
      ref="globalSearchPanel"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      class="w-full relative isolate"
    >
      <div class="flex items-start justify-start w-full">
        <div class="w-full flex flex-col items-center gap-y-2">
          <div class="flex w-full items-center justify-start z-10 gap-x-3">
            <div class="shrink-0 lg:hidden relative">
              <button
                type="button"
                aria-haspopup="true"
                class="flex items-center justify-center w-10 h-10 rounded-full overflow-hidden hover:ring-neutral-900/20 active:bg-neutral-900/20 active:ring-2 active:ring-neutral-900/20 duration-600 focus:outline-none transition"
                @click="blur"
              >
                <div class="rounded-full flex items-center justify-center bg-white w-10 h-10">
                  <fa
                    :icon="['fal', 'xmark']"
                    class="text-xl text-neutral-600 w-6 h-6"
                  />
                </div>
              </button>
            </div>
            <vetster-shine-border
              class-name="w-full"
              :color="isGlobalSearchFocused ? '#FFFFFF' : ['#A07CFE', '#FF48A8', '#FFBE7B']"
              :border-radius="9999"
              :border-width="isGlobalSearchFocused ? 0 : 1"
            >
              <div
                ref="searchInputContainer"
                class="w-full h-10 transition-all relative text-sm/4 text-neutral-950 z-10 before:fixed before:inset-x-0 before:border-b before:z-20 before:top-[calc(4rem-1px)] lg:before:hidden"
                @mousedown="trackEvent('global_search_click')"
              >
                <div class="size-5 flex items-center justify-center absolute left-4 top-2.5">
                  <fa
                    :icon="['fal', 'magnifying-glass']"
                    class="text-lg text-neutral-500 pointer-events-none"
                  />
                </div>
                <input
                  ref="searchInput"
                  v-model="query"
                  class="border border-neutral-200 lg:border-0 w-full text-sm/4 px-2 h-10 bg-white rounded-full pl-11 pr-4 focus:border-blue-600 focus:ring-1 lg:focus:ring-2 focus:ring-blue-600 placeholder-neutral-500"
                  placeholder="Search Vetster"
                  @keyup.up.prevent.stop="prevIndex(results.length)"
                  @focusin="focusGlobalSearch"
                  @keyup.down.prevent.stop="nextIndex(results.length)"
                  @keyup.enter.prevent.stop="handleEnter"
                  @keydown.up.prevent
                  @keydown.down.prevent
                />
                <div class="text-neutral-950 text-xl *:size-5 *:flex *:items-center *:justify-center *:absolute *:top-2.5">
                  <div
                    v-if="loading"
                    class="right-0"
                  >
                    <fa
                      :icon="['fal', 'circle-notch']"
                      class="absolute right-4 pointer-events-none fa-spin"
                    />
                  </div>
                  <button
                    v-else-if="hasQuery"
                    type="button"
                    class="global-search cursor-pointer right-4 hover:text-neutral-500"
                    @click.stop="resetQuery"
                  >
                    <fa :icon="['fal', 'times']" />
                  </button>
                  <div
                    v-else-if="!shouldDimBackdrop"
                    class="right-4 hidden lg:flex"
                  >
                    <div class="pointer-events-none flex select-none items-center justify-center rounded border bg-white py-1 font-mono text-[0.7rem]/4 font-normal text-neutral-500 dark:bg-white/25 dark:text-white border-neutral-200 px-2.5 dark:border-ocean-50">
                      <span class="sr-only">Press slash key to search</span>
                      <span>/</span>
                    </div>
                  </div>
                </div>
              </div>
            </vetster-shine-border>
          </div>
          <transition
            name="fade-fast"
            appear
          >
            <div
              v-if="shouldDimBackdrop"
              ref="backdrop"
              class="fixed inset-0 w-full h-full lg:block z-[1]"
              :class="{
                'bg-neutral-50 lg:bg-ocean-950/75': hasExecutedQuery,
                'bg-ocean-950/75': !hasExecutedQuery,
              }"
              data-navbar-anchor="top"
            />
          </transition>
          <transition
            name="fade-fast"
            appear
          >
            <div
              v-if="shouldDimBackdrop"
              class="fixed inset-0 w-full h-16 bg-neutral-50 lg:hidden z-[1]"
            />
          </transition>
          <div
            ref="searchResult"
            role="dialog"
            aria-modal="true"
            aria-label="Search results"
            class="w-full absolute top-16 lg:top-12 left-0 z-10 lg:max-w-lg"
          >
            <transition name="none">
              <div
                v-if="hasQuery && hasResults"
                class="@container lg:bg-white lg:border lg:border-neutral-300 lg:max-h-2xl lg:shadow-2xl lg:rounded-2xl size-full scroll-pt-11 overflow-y-auto"
              >
                <div
                  v-if="hasPills"
                  class="pt-px px-1 lg:px-3 lg:pt-3.5"
                >
                  <div class="flex space-x-2">
                    <vetster-global-search-pill
                      v-for="(pill, i) in getPills"
                      :key="`pill-${i}`"
                      :item="pill"
                    />
                  </div>
                  <div class="border-b border-neutral-200 w-full mt-3 pt-px" />
                </div>

                <div class="py-3 px-1 lg:p-3">
                  <ul class="text-sm text-neutral-800 space-y-1">
                    <vetster-global-search-tile
                      v-for="(item, i) in results"
                      :key="`item-${i}`"
                      :item="item"
                      :index="i"
                      :data-active="getHighlightedIndex === i"
                      @click="handleResultClick"
                    />
                  </ul>
                </div>
              </div>
              <div
                v-else-if="!hasResults && hasExecutedQuery"
                class="lg:bg-white lg:border lg:border-neutral-300 px-2 lg:px-2 py-6 space-y-6 lg:shadow-2xl lg:rounded-2xl w-full overflow-auto"
              >
                <div class="lg:px-6">
                  <fa
                    :icon="['fal', 'face-sad-tear']"
                    class="text-4xl text-ocean-700 pointer-events-none"
                  />
                  <p class="font-title text-xl mt-4 font-semibold text-neutral-900">
                    We couldn’t find any matching results
                  </p>
                  <p class="mt-2 text-neutral-600">
                    Try searching for a different name, symptom, condition,
                    location or term.
                  </p>
                </div>
                <div class="border-t border-neutral-200 w-full" />
                <div class="lg:px-6">
                  <div>
                    Suggestions
                  </div>
                  <div class="flex items-start">
                    <vetster-button
                      v-cloak
                      :href="guidedSearchUrl"
                      type="a"
                      class="mt-4 space-x-1"
                      size="xl"
                      variant="outlineOcean"
                      rel="nofollow"
                    >
                      <fa
                        :icon="['fal', 'magnifying-glass']"
                        class="fa-fw text-lg"
                      />
                      <span>
                        Find a vet near me
                      </span>
                    </vetster-button>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCat,
  faCircleNotch,
  faCity,
  faDog,
  faEarthAmericas,
  faFaceSadTear,
  faHeartCircleExclamation,
  faNewspaper,
  faRouteInterstate,
  faSearch,
  faStethoscope,
  faUserDoctor,
  faUserNurse,
} from '@fortawesome/pro-light-svg-icons';
import debounce from 'lodash/debounce';
import flatMap from 'lodash/flatMap';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';

import VetsterGlobalSearchPill from '@/components/GlobalSearch/VetsterGlobalSearchPill';
import VetsterGlobalSearchTile from '@/components/GlobalSearch/VetsterGlobalSearchTile';
import VetsterShineBorder from '@/components/VetsterShineBorder';
import {
  forgetCookie,
  getCookie,
  setCookie,
} from '@/helpers/cookies';
import ClickAway from '@/mixins/ClickAway';
import InteractsWithGlobalSearch from '@/mixins/InteractsWithGlobalSearch';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';

library.add({
  faCat,
  faCircleNotch,
  faCity,
  faDog,
  faEarthAmericas,
  faFaceSadTear,
  faHeartCircleExclamation,
  faNewspaper,
  faRouteInterstate,
  faSearch,
  faStethoscope,
  faUserDoctor,
  faUserNurse,
});

export default {
  name: 'VetsterGlobalSearch',

  components: {
    VetsterGlobalSearchTile,
    VetsterGlobalSearchPill,
    VetsterShineBorder,
  },

  mixins: [
    ClickAway,
    InteractsWithGlobalSearch,
    LocalizesRoutes,
  ],

  props: {
    isCondition: {
      type: Boolean,
      default: false,
    },
    isGlossary: {
      type: Boolean,
      default: false,
    },
    isSymptom: {
      type: Boolean,
      default: false,
    },
    species: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      controller: null,
      loading: false,
      results: [],
      queryCompleted: '',
      query: '',
      minInputChars: 1,
    };
  },

  computed: {
    shouldDimBackdrop() {
      return this.isGlobalSearchFocused || this.hasExecutedQuery;
    },
    hasExecutedQuery() {
      return this.hasQuery && this.queryCompleted !== '';
    },
    hasResults() {
      return this.results.length > 0;
    },
    getPills() {
      const uniqueSpecies = new Set();

      const speciesList = flatMap(this.results, (result) => reduce(result.species_types || [], (acc, species) => {
        if (!uniqueSpecies.has(species.value)) {
          uniqueSpecies.add(species.value);
          acc.push({
            name: species.name,
            value: species.value,
            type: result.type,
          });
        }

        return acc;
      }, []));

      const priorityMap = {
        DOG: 0,
        CAT: 1,
      };

      return orderBy(speciesList, [
        (species) => (priorityMap[species.value] !== undefined ? priorityMap[species.value] : 2),
        'value',
      ]);
    },
    hasPills() {
      return this.getPills?.length > 0;
    },
    hasQuery() {
      return this.query.length >= this.minInputChars;
    },
    getPayload() {
      return {
        filter: {
          query: this.query,
          species_types: Object.keys(this.getFacets),
        },
        per_page: 8,
      };
    },
  },

  watch: {
    shouldDimBackdrop(value) {
      if (value) {
        this.$nextTick(this.$layout.updateNavbarAnchors);
        this.$bus.$emit('disable-scroll');
      } else {
        this.$bus.$emit('enable-scroll');
      }
    },
    query() {
      this.fetchResults();
    },
    isGlobalSearchFocused(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.$refs?.searchInput.focus();
        });
      }
    },
  },

  created() {
    this.$bus.$on('escape', this.handleEscape);
    this.$bus.$on('global-search:facet-selected', this.handleFacetSelect);
  },

  mounted() {
    this.setFacetFromCookie();

    document.addEventListener('click', this.registerClickAway);
    document.addEventListener('keydown', this.handleSlash);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleSlash);
    this.$bus.$off('escape', this.handleEscape);
    this.$bus.$off('global-search:facet-selected', this.handleFacetSelect);
  },

  methods: {
    handleEnter() {
      if (!this.loading && this.hasResults && !this.isLoadingResult) {
        const item = this.results[this.getHighlightedIndex];

        if (item?.url) {
          this.setLoadingResult(this.getHighlightedIndex);
          window.location.href = item.url;
          setTimeout(() => {
            this.$store.commit('globalSearch/resetIndex');
          }, 100);
        }
      }
    },
    handleSlash(event) {
      if (event.key === '/' && this.$refs?.searchInput !== document.activeElement) {
        event.preventDefault();
        this.$refs?.searchInput?.focus();
      }
    },
    handleFacetSelect(facet) {
      if (getCookie('v-global-search-facet') === facet) {
        forgetCookie('v-global-search-facet');
      } else {
        setCookie('v-global-search-facet', facet, '1h');
      }

      this.fetchResults();
    },
    registerClickAway(event) {
      this.hideOnClickAway(
        event,
        this.$refs?.searchResult,
        this.handleClickAway,
        [
          this.$refs?.searchInputContainer,
          document.querySelector('.global-search'),
        ],
      );
    },
    resetQuery() {
      this.reset();
      this.$refs?.searchInput?.focus();
    },
    reset() {
      this.queryCompleted = '';
      this.query = '';
      this.results = [];
      this.loading = false;
      this.resetIndex();
      this.resetFacets();
    },
    setFacetFromCookie() {
      const facet = getCookie('v-global-search-facet');

      if (facet) {
        this.toggleFacet(facet);
      }
    },
    handleClickAway() {
      if (this.$screen('lg') || !this.hasExecutedQuery) {
        this.blur();
      }
    },
    handleEscape() {
      this.blur();
    },
    blur() {
      this.reset();
      this.$refs.searchInput.blur();
      this.blurGlobalSearch();
    },
    fetchResults: debounce(
      async function search() {
        this.controller?.abort();

        if (this.query === '' || this.query?.length < this.minInputChars) {
          this.results = [];
          this.queryCompleted = '';

          return;
        }

        this.controller = new AbortController();
        const { signal } = this.controller;
        const payload = this.getPayload;
        this.loading = true;

        await this.$api
          .get('p/search/global', { params: payload }, { signal })
          .then((response) => {
            this.results = response.data.data;
            this.queryCompleted = payload.query;
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false;
            this.controller = null;
          });
      },
      200,
      {
        maxWait: 700,
      },
    ),
    handleResultClick(item) {
      this.trackEvent('global_search_result_click', {
        query: this.query,
        url: item?.url,
        referrer_url: window.location.href,
      });
    },
    trackEvent(eventName, attributes) {
      this.$analytics.track({
        eventName,
        parent: 'navbar',
        child: 'global-search',
        ...attributes,
      });
    },
  },
};
</script>
